import React from 'react';

interface SummaryCardProps {
  id?: string;
  label: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  variant?: 'primary' | 'emerald' | 'amber' | 'rose' | 'sky';
  badge?: string;
  onClick?: () => void;
}

export const SummaryCard: React.FC<SummaryCardProps> = ({
  id,
  label,
  value,
  subtitle,
  icon,
  variant = 'primary',
  badge,
  onClick,
}) => {
  const getThemeClasses = () => {
    switch (variant) {
      case 'emerald':
        return {
          iconBg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
          accent: 'hover:border-emerald-500/30',
        };
      case 'amber':
        return {
          iconBg: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
          accent: 'hover:border-amber-500/30',
        };
      case 'rose':
        return {
          iconBg: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
          accent: 'hover:border-rose-500/30',
        };
      case 'sky':
        return {
          iconBg: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
          accent: 'hover:border-sky-500/30',
        };
      case 'primary':
      default:
        return {
          iconBg: 'bg-primary-500/10 text-primary-400 border-primary-500/20',
          accent: 'hover:border-primary-500/30',
        };
    }
  };

  const theme = getThemeClasses();

  return (
    <div
      id={id}
      onClick={onClick}
      className={`rounded-2xl p-5 border border-white/10 bg-surface-800/80 backdrop-blur-sm transition-all duration-200
                  ${onClick ? 'cursor-pointer hover:-translate-y-0.5 shadow-lg shadow-black/20' : ''} ${theme.accent}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
          <p className="mt-2 text-3xl font-extrabold text-white tracking-tight">{value}</p>
          {subtitle && (
            <p className="mt-1 text-xs text-slate-400 flex items-center gap-1.5">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl border ${theme.iconBg} flex items-center justify-center shrink-0`}>
          {icon}
        </div>
      </div>
      {badge && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
          <span className="text-slate-500 font-medium">{badge}</span>
        </div>
      )}
    </div>
  );
};
